/**
 * The top bar — 32px (`110-design-system.md` §5). Breadcrumb, the sync indicator, the pane
 * toggles, the theme control, the user chip. "It is not a place for a logo."
 */
import { Laptop, LogOut, Moon, PanelLeft, PanelRight, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SyncIndicator } from '@/components/shell/SyncIndicator';
import { useSession, useLogout } from '@/features/auth/useSession';
// M7: the theme cycle must PERSIST. `preferences.tsx` writes localStorage (the pre-paint read
// path) AND queues the `settings.patch`, so a new browser inherits the choice (`110` §2.1).
import { useThemePreference } from '@/features/settings/preferences';
import type { Theme } from '@/stores/theme';
import { useTranslation, type MessageKey } from '@/lib/i18n';

const NEXT_THEME: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' };

const THEME_ICON: Record<Theme, React.ReactNode> = {
  system: <Laptop className="size-[var(--icon)]" strokeWidth={1.75} />,
  light: <Sun className="size-[var(--icon)]" strokeWidth={1.75} />,
  dark: <Moon className="size-[var(--icon)]" strokeWidth={1.75} />,
};

const THEME_LABEL_KEY: Record<Theme, MessageKey> = {
  system: 'shell.topbar.theme.system',
  light: 'shell.topbar.theme.light',
  dark: 'shell.topbar.theme.dark',
};

interface TopBarProps {
  sidebarCollapsed: boolean;
  rightCollapsed: boolean;
  onToggleSidebar: () => void;
  onToggleRight: () => void;
}

export function TopBar({
  sidebarCollapsed,
  rightCollapsed,
  onToggleSidebar,
  onToggleRight,
}: TopBarProps) {
  const { theme, setTheme } = useThemePreference();
  const { data: session } = useSession();
  const { mutate: signOut, isPending: signingOut } = useLogout();
  const { t } = useTranslation();

  const themeLabel = t(THEME_LABEL_KEY[theme]);
  const nextThemeLabel = t(THEME_LABEL_KEY[NEXT_THEME[theme]]);

  return (
    <header className="flex h-8 shrink-0 items-center gap-1 border-b border-[var(--border)] px-1">
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleSidebar}
        aria-label={t(sidebarCollapsed ? 'shell.topbar.sidebar.show' : 'shell.topbar.sidebar.hide')}
        aria-pressed={!sidebarCollapsed}
        title={t('shell.topbar.sidebar.toggleTitle')}
      >
        <PanelLeft strokeWidth={1.75} />
      </Button>

      <span className="truncate text-[var(--muted-foreground)]">
        {t('shell.topbar.breadcrumb')}
      </span>

      {/* 110 §9: immediately right of the breadcrumb, always visible, on EVERY route. */}
      <SyncIndicator />

      <div className="ml-auto flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            setTheme(NEXT_THEME[theme]);
          }}
          aria-label={t('shell.topbar.theme.aria', { theme: themeLabel, next: nextThemeLabel })}
          title={t('shell.topbar.theme.title', { theme: themeLabel, next: nextThemeLabel })}
        >
          {THEME_ICON[theme]}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleRight}
          aria-label={t(
            rightCollapsed ? 'shell.topbar.rightPanel.show' : 'shell.topbar.rightPanel.hide',
          )}
          aria-pressed={!rightCollapsed}
          title={t('shell.topbar.rightPanel.toggleTitle')}
        >
          <PanelRight strokeWidth={1.75} />
        </Button>

        {session && (
          <>
            <span className="px-1 text-[var(--muted-foreground)]">{session.user.displayName}</span>
            <Button
              variant="ghost"
              size="icon"
              disabled={signingOut}
              onClick={() => {
                signOut();
              }}
              aria-label={t('shell.topbar.signOut')}
              title={t('shell.topbar.signOut')}
            >
              <LogOut strokeWidth={1.75} />
            </Button>
          </>
        )}
      </div>
    </header>
  );
}
